import { useEffect, useRef, useState } from "react";
import maplibregl from "maplibre-gl";
import { getSavedStyle, getStyleSpec, type MapStyle } from "@/lib/map-styles";

interface UseMapInitOptions {
  controlsPosition?: "top-right" | "bottom-right";
  onBeforeStyleChange?: () => void;
}

export function useMapInit(
  containerRef: React.RefObject<HTMLDivElement>,
  options: UseMapInitOptions = {}
) {
  const map = useRef<maplibregl.Map | null>(null);
  const [currentStyle, setCurrentStyle] = useState(getSavedStyle);
  const onBeforeStyleChangeRef = useRef(options.onBeforeStyleChange);
  onBeforeStyleChangeRef.current = options.onBeforeStyleChange;

  const handleStyleChange = (style: MapStyle) => {
    if (!map.current || style.id === currentStyle.id) return;
    localStorage.setItem("mapStyleId", style.id);
    onBeforeStyleChangeRef.current?.();
    setCurrentStyle(style);
    map.current.setStyle(getStyleSpec(style), { diff: false });
  };

  useEffect(() => {
    if (!containerRef.current) return;
    const savedStyle = getSavedStyle();
    const pos = options.controlsPosition ?? "top-right";
    const savedCenter = JSON.parse(localStorage.getItem("mapCenter") || "[0,0]") as [number, number];
    const savedZoom = parseFloat(localStorage.getItem("mapZoom") || "11");

    map.current = new maplibregl.Map({
      container: containerRef.current,
      style: getStyleSpec(savedStyle),
      center: savedCenter,
      zoom: savedZoom,
      attributionControl: { compact: false },
    });

    map.current.addControl(new maplibregl.NavigationControl(), pos);
    map.current.addControl(
      new maplibregl.GeolocateControl({
        positionOptions: { enableHighAccuracy: true },
        trackUserLocation: true,
      }),
      pos
    );

    map.current.on("moveend", () => {
      if (!map.current) return;
      const { lng, lat } = map.current.getCenter();
      localStorage.setItem("mapCenter", JSON.stringify([lng, lat]));
      localStorage.setItem("mapZoom", String(map.current.getZoom()));
    });

    return () => {
      map.current?.remove();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { map, currentStyle, handleStyleChange };
}
